import { useSEO } from "../lib/useSEO";
import PageHero from "../components/layout/PageHero";
import SectionHeader from "../components/SectionHeader";
import Icon from "../components/Icon";
import { PartnerForm } from "../components/Forms";
import { Reveal } from "../components/motion/Reveal";

const WHY_PARTNER = [
  { icon: "Users", title: "Qualified Student Pipeline", text: "Meet students who are counselled, prepared and genuinely matched to your programs." },
  { icon: "Globe2", title: "Regional Reach", text: "Connect with prospective students across Suvera Academy's growing markets." },
  { icon: "Handshake", title: "Transparent Collaboration", text: "Clear processes, honest communication and shared standards of student care." },
  { icon: "FileCheck2", title: "Quality Applications", text: "Complete, verified applications that respect your admissions team's time." },
];

const MODEL_STEPS = [
  { step: "01", title: "Introduce", text: "Share your institution's profile, programs and recruitment priorities with our team." },
  { step: "02", title: "Align", text: "We agree on a partnership model — representation, referral or collaboration — that fits both sides." },
  { step: "03", title: "Grow", text: "Receive counselled, well-prepared applicants with ongoing communication and support." },
];

const OFFERINGS = [
  "Student recruitment and representation",
  "Application processing and documentation support",
  "Market insight and student demand feedback",
  "Event, webinar and school-visit coordination",
  "Pre-departure briefings for enrolled students",
  "Dedicated partner communication channel",
];

export default function Partner() {
  useSEO(
    "Partner With Us — Suvera Academy",
    "Partnership opportunities for universities, colleges, education providers and recruitment partners with Suvera Academy."
  );
  return (
    <main data-testid="partner-page">
      <PageHero
        eyebrow="Partner With Us"
        title="Become a Partner"
        description="Suvera Academy works with universities, colleges, education providers, recruitment partners and institutional representatives who share our standards of student care."
        breadcrumbs={[{ label: "Home", to: "/" }, { label: "Partner With Us" }]}
        testid="partner-hero"
      />

      <section className="mx-auto max-w-[1440px] px-4 py-16 sm:px-6 lg:px-10 lg:py-24">
        <SectionHeader chapter="Why" eyebrow="Partnership" title="Why Partner With Suvera Academy?" />
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {WHY_PARTNER.map((w, i) => (
            <Reveal key={w.title} delay={i * 0.08} className="h-full">
              <div className="group h-full rounded-2xl border border-slate-200 bg-white p-7 transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1.5 hover:border-navy/30 hover:shadow-[0_20px_50px_-16px_rgba(13,24,51,0.18)]" data-testid={`partner-benefit-${w.title.toLowerCase().replace(/\s+/g, "-")}`}>
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-navy/5 text-navy transition-colors duration-300 group-hover:bg-navy group-hover:text-white">
                  <Icon name={w.icon} className="h-6 w-6" />
                </span>
                <h3 className="mt-5 font-display text-xl font-semibold text-navy-dark">{w.title}</h3>
                <p className="mt-2.5 text-sm leading-relaxed text-slate-600">{w.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-sand">
        <div className="mx-auto max-w-[1440px] px-4 py-16 sm:px-6 lg:px-10 lg:py-24">
          <div className="grid grid-cols-1 gap-14 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <SectionHeader chapter="Model" eyebrow="How It Works" title="Partnership Model" />
              <div className="space-y-6">
                {MODEL_STEPS.map((s, i) => (
                  <Reveal key={s.step} delay={i * 0.08}>
                    <div className="flex gap-5">
                      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-navy font-mono-accent text-xs font-bold text-white">{s.step}</span>
                      <div>
                        <h3 className="font-display text-lg font-semibold text-navy-dark">{s.title}</h3>
                        <p className="mt-1 text-sm leading-relaxed text-slate-600">{s.text}</p>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
            <div className="lg:col-span-7">
              <SectionHeader chapter="Offer" eyebrow="What We Offer" title="What Partners Can Expect" />
              <Reveal>
                <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="partner-offerings">
                  {OFFERINGS.map((o) => (
                    <li key={o} className="flex items-start gap-2.5 rounded-xl border border-slate-200 bg-white p-4 text-sm font-medium text-slate-700">
                      <Icon name="CheckCircle2" className="mt-0.5 h-4 w-4 shrink-0 text-dot-green" />
                      {o}
                    </li>
                  ))}
                </ul>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1440px] px-4 py-16 sm:px-6 lg:px-10 lg:py-24">
        <div className="mx-auto max-w-3xl">
          <SectionHeader
            chapter="Enquire"
            eyebrow="Partner Enquiry"
            title="Start the Conversation"
            description="Tell us about your organisation and partnership goals. Our team will respond to discuss next steps."
          />
          <Reveal>
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-[0_8px_32px_rgba(27,47,91,0.06)] sm:p-10">
              <PartnerForm />
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
